import React, { useState, useEffect } from 'react';
import { Upload, Palette, Building2, Globe, Save, AlertCircle, CheckCircle, X, Eye, Mail, User, Camera } from 'lucide-react';
import api from '../utils/api';
import WhiteLabelEmailDomains from './WhiteLabelEmailDomains';


const WhiteLabelBrandingSettings = ({ user }) => {
  const [branding, setBranding] = useState({
    enabled: false, // Master toggle for white label portal
    company_name: 'Your Company',
    logo_url: null,
    primary_color: '#6B46C1',
    secondary_color: '#EC4899',
    background_color: '#FFFFFF',
    text_color: '#1F2937',
    accent_color: '#10B981',
    custom_domain: '',
    support_email: '',
    support_phone: '',
    website_url: '',
    welcome_message: 'Welcome to your project portal',
    footer_text: '', // Changed from empty to ensure it's always empty initially
    // Email branding settings
    email_from_name: '',
    email_from_address: '',
    email_reply_to: '',
    email_logo_url: '',
    email_header_color: '#6B46C1',
    email_footer_text: '',
    enable_email_branding: false
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    // Force refresh branding data to get clean backend data
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/branding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data) {
        // Ensure all fields have string values to prevent controlled/uncontrolled input issues
        const cleanData = {
          enabled: false,
          company_name: 'Your Company',
          logo_url: null,
          primary_color: '#6B46C1',
          secondary_color: '#EC4899',
          background_color: '#FFFFFF',
          text_color: '#1F2937',
          accent_color: '#10B981',
          custom_domain: '',
          support_email: '',
          support_phone: '',
          website_url: '',
          welcome_message: 'Welcome to your project portal',
          footer_text: '',
          email_from_name: '',
          email_from_address: '',
          email_reply_to: '',
          email_logo_url: '',
          email_header_color: '#6B46C1',
          email_footer_text: '',
          enable_email_branding: false,
          // Override with actual data from API, ensuring strings are never null/undefined
          ...response.data,
          footer_text: response.data.footer_text && response.data.footer_text.includes('CCCC') ? '' : (response.data.footer_text || ''),
          logo_url: response.data.logo_url || null,
          // Ensure all text fields are strings, never null/undefined
          company_name: response.data.company_name || 'Your Company',
          support_email: response.data.support_email || '',
          support_phone: response.data.support_phone || '',
          website_url: response.data.website_url || '',
          welcome_message: response.data.welcome_message || 'Welcome to your project portal',
          email_from_name: response.data.email_from_name || '',
          email_from_address: response.data.email_from_address || '',
          email_reply_to: response.data.email_reply_to || '',
          email_footer_text: response.data.email_footer_text || '',
        };
        
        setBranding(cleanData);
        setLogoPreview(cleanData.logo_url);
      }
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      showNotification('error', 'Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 5000);
  };

  const handleInputChange = (field, value) => {
    setBranding(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColorChange = (field, value) => {
    // Ensure the value starts with #
    const color = value.startsWith('#') ? value : `#${value}`;
    handleInputChange(field, color);
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showNotification('error', 'Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showNotification('error', 'File size must be less than 5MB');
      return;
    }

    setLogoFile(file);
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);
    
    setUploadingLogo(true);
    
    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/branding/upload-logo`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setBranding(prev => ({
        ...prev,
        logo_url: response.data.logo_url
      }));

      showNotification('success', 'Logo uploaded successfully');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      showNotification('error', 'Failed to upload logo');
      setLogoPreview(null);
      setLogoFile(null);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setBranding(prev => ({
      ...prev,
      logo_url: null
    }));
    setLogoPreview(null);
    setLogoFile(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Validate required fields
      if (!branding.company_name?.trim()) {
        showNotification('error', 'Company name is required');
        setSaving(false);
        return;
      }

      await api.put(`/branding`, branding, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      showNotification('success', 'Branding settings saved successfully');
    } catch (error) {
      console.error('Failed to save branding:', error);
      showNotification('error', 'Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePortalPreview = async () => {
    try {
      const response = await api.get('/admin/portal/preview');
      if (!response.data?.success) {
        showNotification('error', response.data?.error || 'Unable to preview portal. Please check portal settings.');
        return;
      }

      const portalData = {
        token: response.data.token,
        contact: response.data.contact,
        client: response.data.client,
        team_id: response.data.team_id
      };
      const encodedData = btoa(JSON.stringify(portalData));
      const portalUrl = `${window.location.origin}/portal/${response.data.team_id}/dashboard#auth=${encodedData}`;
      window.open(portalUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening portal preview:', error);
      showNotification('error', 'Failed to open portal preview. Please try again.');
    }
  };

  const resetToDefaults = () => {
    setBranding({
      company_name: 'Your Company',
      logo_url: null,
      primary_color: '#6B46C1',
      secondary_color: '#EC4899',
      background_color: '#FFFFFF',
      text_color: '#1F2937',
      accent_color: '#10B981',
      custom_domain: '',
      support_email: '',
      support_phone: '',
      website_url: '',
      welcome_message: 'Welcome to your project portal',
      footer_text: '',
      // Email branding settings
      email_from_name: '',
      email_from_address: '',
      email_reply_to: '',
      email_logo_url: '',
      email_header_color: '#6B46C1',
      email_footer_text: '',
      enable_email_branding: false
    });
    setLogoPreview(null);
    setLogoFile(null);
  };

  const BrandingPreview = () => (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
      <h4 className="font-medium text-gray-900 mb-4">Client Portal Preview</h4>
      
      <div 
        className="border rounded-lg overflow-hidden"
        style={{ backgroundColor: branding.background_color }}
      >
        {/* Header */}
        <div 
          className="p-4 border-b"
          style={{ 
            backgroundColor: branding.primary_color,
            color: branding.background_color
          }}
        >
          <div className="flex items-center space-x-3">
            {(branding.logo_url || logoPreview) && (
              <img
                src={logoPreview || branding.logo_url}
                alt="Logo"
                className="h-8 w-auto"
              />
            )}
            <h1 className="text-lg font-semibold">
              {branding.company_name}
            </h1>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4">
          <h2 
            className="text-xl font-medium mb-2"
            style={{ color: branding.text_color }}
          >
            {branding.welcome_message}
          </h2>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div 
              className="p-3 rounded-lg"
              style={{ backgroundColor: branding.secondary_color + '20' }}
            >
              <div className="text-sm font-medium" style={{ color: branding.text_color }}>
                Projects
              </div>
              <div className="text-2xl font-bold" style={{ color: branding.secondary_color }}>
                12
              </div>
            </div>
            
            <div 
              className="p-3 rounded-lg"
              style={{ backgroundColor: branding.accent_color + '20' }}
            >
              <div className="text-sm font-medium" style={{ color: branding.text_color }}>
                Tasks
              </div>
              <div className="text-2xl font-bold" style={{ color: branding.accent_color }}>
                48
              </div>
            </div>
          </div>
          
          <button
            className="mt-4 px-4 py-2 rounded-lg text-white font-medium"
            style={{ backgroundColor: branding.primary_color }}
          >
            View Projects
          </button>
        </div>
        
        {/* Footer */}
        {branding.footer_text && (
          <div 
            className="p-3 border-t text-center text-sm"
            style={{ color: branding.text_color + 'CC' }}
          >
            {branding.footer_text}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 border border-green-200 text-green-800'
            : 'bg-red-100 border border-red-200 text-red-800'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{notification.message}</span>
          <button
            onClick={() => setNotification({ show: false, type: '', message: '' })}
            className="text-current opacity-70 hover:opacity-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Status Banner */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mb-6">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-green-900 mb-1">
              White Label Features Available
            </h3>
            <p className="text-sm text-green-700 mb-2">
              Customize your client portal branding and send emails from your own verified domain.
            </p>
            <div className="flex items-center space-x-2 text-xs text-green-600">
              <span className="px-2 py-1 bg-white/50 rounded">✓ Portal Logo & Colors</span>
              <span className="px-2 py-1 bg-white/50 rounded">✓ Custom Email Domain</span>
              <span className="px-2 py-1 bg-white/50 rounded">✓ Custom Sender Name</span>
            </div>
          </div>
        </div>
      </div>

      {/* White Label Email Domains Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
        <WhiteLabelEmailDomains 
          user={user} 
          onNotification={(type, message) => showNotification(type, message)}
        />
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">Enable White Label Portal</h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={branding.enabled}
                  onChange={(e) => handleInputChange('enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>
            <p className="text-sm text-gray-600">
              {branding.enabled 
                ? "✓ Portal is using your custom branding. Disable to use default Athena branding." 
                : "Portal is using default Athena branding. Enable to apply your custom brand."}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <h2 className="text-xl font-semibold text-gray-900">White-Label Client Portal</h2>
              <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">Admin Only</span>
            </div>
            <p className="text-gray-600">Customize your client portal branding and appearance</p>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Eye className="w-4 h-4" />
              <span>{showPreview ? 'Hide' : 'Show'} Preview</span>
            </button>
            
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 transition-all shadow-md"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{saving ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </div>

        <div className={`grid ${showPreview ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'} gap-8`}>
          {/* Settings Form */}
          <div className="space-y-6">
            {/* Company Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                <Building2 className="w-5 h-5" />
                <span>Company Information</span>
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={branding.company_name}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Your Company Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Welcome Message
                  </label>
                  <input
                    type="text"
                    value={branding.welcome_message}
                    onChange={(e) => handleInputChange('welcome_message', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Welcome to your project portal"
                  />
                </div>

                {/* Preview Portal Button */}
                <div className="pt-2">
                  <button
                    onClick={handlePortalPreview}
                    className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                  >
                    <Eye className="w-4 h-4" />
                    <span>Preview Client Portal</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Logo Upload */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                <Upload className="w-5 h-5" />
                <span>Company Logo</span>
              </h3>
              
              <div className="space-y-4">
                {(branding.logo_url || logoPreview) ? (
                  <div className="space-y-4">
                    {/* Current Logo Display - Larger and More Prominent */}
                    <div className="flex items-center justify-center p-6 bg-gray-50 border-2 border-gray-200 rounded-lg">
                      <img
                        src={logoPreview || branding.logo_url}
                        alt="Current Company Logo"
                        className="max-w-48 max-h-24 object-contain"
                      />
                    </div>
                    
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-900 mb-1">Current Logo</p>
                      <p className="text-xs text-gray-500 mb-4">This logo will appear in your client portal header</p>
                      
                      <div className="flex items-center justify-center space-x-3">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            disabled={uploadingLogo}
                          />
                          <span className="inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors">
                            <Camera className="w-4 h-4" />
                            <span>{uploadingLogo ? 'Uploading...' : 'Replace Logo'}</span>
                          </span>
                        </label>
                        
                        <button
                          onClick={handleRemoveLogo}
                          disabled={uploadingLogo}
                          className="inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          <span>Remove</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={uploadingLogo}
                      />
                      
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-400 hover:bg-purple-50 transition-colors">
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-2 font-medium">
                          {uploadingLogo ? 'Uploading logo...' : 'Upload Your Company Logo'}
                        </p>
                        <p className="text-sm text-gray-500">
                          PNG, JPG, or SVG up to 5MB • Recommended: 200x80px
                        </p>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Color Scheme */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                <Palette className="w-5 h-5" />
                <span>Color Scheme</span>
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { key: 'primary_color', label: 'Primary Color', description: 'Main brand color' },
                  { key: 'secondary_color', label: 'Secondary Color', description: 'Accent color' },
                  { key: 'accent_color', label: 'Success Color', description: 'For positive actions' },
                  { key: 'background_color', label: 'Background', description: 'Page background' },
                  { key: 'text_color', label: 'Text Color', description: 'Primary text' }
                ].map(({ key, label, description }) => (
                  <div key={key} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {label}
                    </label>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={branding[key]}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                        className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer"
                      />
                      
                      <input
                        type="text"
                        value={branding[key]}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="#000000"
                      />
                    </div>
                    
                    <p className="text-xs text-gray-500">{description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Settings */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Settings</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Footer Text
                  </label>
                  <input
                    type="text"
                    value={branding.footer_text || ''}
                    onChange={(e) => handleInputChange('footer_text', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder=""
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional footer text that appears at the bottom of your client portal
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Support Email
                    </label>
                    <input
                      type="email"
                      value={branding.support_email}
                      onChange={(e) => handleInputChange('support_email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="support@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Support Phone
                    </label>
                    <input
                      type="tel"
                      value={branding.support_phone}
                      onChange={(e) => handleInputChange('support_phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Website URL
                    </label>
                    <input
                      type="url"
                      value={branding.website_url}
                      onChange={(e) => handleInputChange('website_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="https://company.com"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Email Branding Section */}
            <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                    <Mail className="w-5 h-5" />
                    <span>Email Branding (Premium)</span>
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Customize email notifications sent to your clients with your branding
                  </p>
                </div>
                
                <label className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Enable Custom Emails</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={branding.enable_email_branding}
                      onChange={(e) => handleInputChange('enable_email_branding', e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-12 h-6 rounded-full transition-colors ${
                      branding.enable_email_branding ? 'bg-purple-600' : 'bg-gray-200'
                    }`}>
                      <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                        branding.enable_email_branding ? 'translate-x-6' : 'translate-x-0.5'
                      }`} style={{ marginTop: '2px' }}></div>
                    </div>
                  </div>
                </label>
              </div>

              {branding.enable_email_branding && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Sender Information */}
                    <div className="space-y-4">
                      <h4 className="text-md font-medium text-gray-900">Sender Information</h4>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          From Name
                        </label>
                        <div className="flex items-center space-x-2">
                          <User className="w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={branding.email_from_name}
                            onChange={(e) => handleInputChange('email_from_name', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Your Company Name"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          From Email Address
                        </label>
                        <div className="flex items-center space-x-2">
                          <Mail className="w-5 h-5 text-gray-400" />
                          <input
                            type="email"
                            value={branding.email_from_address}
                            onChange={(e) => handleInputChange('email_from_address', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="notifications@yourcompany.com"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Reply-To Email
                        </label>
                        <div className="flex items-center space-x-2">
                          <Mail className="w-5 h-5 text-gray-400" />
                          <input
                            type="email"
                            value={branding.email_reply_to}
                            onChange={(e) => handleInputChange('email_reply_to', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="support@yourcompany.com"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Email Design */}
                    <div className="space-y-4">
                      <h4 className="text-md font-medium text-gray-900">Email Design</h4>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Logo URL
                        </label>
                        <div className="flex items-center space-x-2">
                          <Camera className="w-5 h-5 text-gray-400" />
                          <input
                            type="url"
                            value={branding.email_logo_url}
                            onChange={(e) => handleInputChange('email_logo_url', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="https://yourcompany.com/logo.png"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Leave empty to use main logo above
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Header Color
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="color"
                            value={branding.email_header_color}
                            onChange={(e) => handleInputChange('email_header_color', e.target.value)}
                            className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={branding.email_header_color}
                            onChange={(e) => handleInputChange('email_header_color', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="#6366f1"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Footer Text
                        </label>
                        <textarea
                          value={branding.email_footer_text}
                          onChange={(e) => handleInputChange('email_footer_text', e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                          placeholder="© 2025 Your Company Name. All rights reserved."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Email Preview */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">Email Preview</h5>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div 
                        className="text-center py-4 mb-4 rounded-lg text-white"
                        style={{ backgroundColor: branding.email_header_color }}
                      >
                        <div className="text-lg font-semibold">
                          {branding.email_from_name || 'Your Company Name'}
                        </div>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Task Update Notification</h3>
                        <p className="text-gray-600 mb-4">
                          Hello John, your task "Website Design Review" has been updated and is ready for your review.
                        </p>
                        <button 
                          className="px-4 py-2 text-white rounded-lg"
                          style={{ backgroundColor: branding.email_header_color }}
                        >
                          View Task
                        </button>
                      </div>
                      
                      <div className="text-center text-xs text-gray-500 border-t border-gray-200 pt-2">
                        {branding.email_footer_text || '© 2025 Your Company Name. All rights reserved.'}
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                      <div className="text-sm text-yellow-800">
                        <strong>Note:</strong> Email branding will apply to all client notifications including task updates, 
                        password resets, and portal invitations. Make sure your email settings are configured correctly 
                        to avoid delivery issues.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
              <button
                onClick={resetToDefaults}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Reset to Defaults
              </button>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {showPreview ? 'Hide' : 'Show'} Preview
                </button>
                
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 transition-all shadow-md"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Preview Panel */}
          {showPreview && (
            <div className="space-y-4">
              <BrandingPreview />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default WhiteLabelBrandingSettings;
